import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react'
import axiosInstance from '../helpers/axios'
import { resolveAnyStaticUrl } from '../urlConfig'
import { fenceColorsUi } from '../store/localItems'
import {
    getPublicBlankPricingSnapshot,
    setPublicBlankPricingSnapshot,
} from '../utils/pricing/calculateProductionPrice'

const DEFAULT_KEY = 'gabbro-diabase'

const normalizeMaterialKey = (value) => {
    const key = String(value || '').trim().toLowerCase()
    return key === 'kapustinsky' ? 'vinga' : key
}

const createLocalFallbackMaterial = (key, name, paletteName, densityKgM3) => ({
    key,
    name,
    aliases: [],
    densityKgM3,
    revision: 0,
    texturePreset: {
        url: fenceColorsUi?.[paletteName] || null,
        path: '',
        repeat: [1, 1],
        offset: [0, 0],
        rotation: 0,
        roughness: 0.72,
        metalness: 0,
        materialKey: key,
        materialName: name,
        densityKgM3,
        catalogRevision: 0,
    },
})

const LOCAL_FALLBACK_MATERIALS = [
    createLocalFallbackMaterial('gabbro-diabase', 'Габбро-диабаз', 'Габбро-диабаз', 3070),
    createLocalFallbackMaterial('dymovsky', 'Дымовский гранит', 'Дымовский', 2700),
    createLocalFallbackMaterial('vinga', 'Винга', 'Винга', 2700),
    createLocalFallbackMaterial('mansurovsky', 'Мансуровский гранит', 'Мансуровский', 2700),
]

const BlankMaterialCatalogContext = createContext({
    materials: LOCAL_FALLBACK_MATERIALS,
    materialsByKey: {},
    texturePalette: fenceColorsUi,
    loading: false,
    error: '',
    defaultMaterialKey: DEFAULT_KEY,
    pricingPolicy: {},
    reload: async () => [],
})

const normalizePair = (value, fallback) => (
    Array.isArray(value) && value.length >= 2
        ? [Number(value[0]) || fallback[0], Number(value[1]) || fallback[1]]
        : [...fallback]
)

const normalizeMaterial = (material = {}) => {
    const key = normalizeMaterialKey(material.key)
    const preset = material.texturePreset && typeof material.texturePreset === 'object'
        ? material.texturePreset
        : {}
    const path = String(preset.path || preset.url || '').trim()
    const revision = Number(material.revision || 0)
    const versionedPath = path
        ? `${path}${path.includes('?') ? '&' : '?'}v=${encodeURIComponent(revision || 0)}`
        : ''

    return {
        ...material,
        key,
        name: String(material.name || key),
        densityKgM3: Number(material.densityKgM3 || 3070),
        texturePreset: {
            url: versionedPath ? resolveAnyStaticUrl(versionedPath) : null,
            path,
            repeat: normalizePair(preset.repeat, [1, 1]),
            offset: normalizePair(preset.offset, [0, 0]),
            rotation: Number(preset.rotation || 0),
            roughness: Number.isFinite(Number(preset.roughness)) ? Number(preset.roughness) : 0.72,
            metalness: Number.isFinite(Number(preset.metalness)) ? Number(preset.metalness) : 0,
            materialKey: key,
            materialName: String(material.name || key),
            densityKgM3: Number(material.densityKgM3 || 3070),
            catalogRevision: revision,
        },
    }
}

export const BlankMaterialCatalogProvider = ({ children }) => {
    const [materials, setMaterials] = useState(LOCAL_FALLBACK_MATERIALS)
    const [loading, setLoading] = useState(false)
    const [error, setError] = useState('')
    const [pricingPolicy, setPricingPolicy] = useState({})

    const reload = useCallback(async () => {
        try {
            setLoading(true)
            setError('')
            const response = await axiosInstance.get('/public/blank-materials')
            const next = (Array.isArray(response?.data?.materials) ? response.data.materials : [])
                .map(normalizeMaterial)
                .filter((material) => material.key)
            const resolved = next.length ? next : LOCAL_FALLBACK_MATERIALS
            const nextPricingPolicy = response?.data?.pricingPolicy && typeof response.data.pricingPolicy === 'object'
                ? response.data.pricingPolicy
                : {}
            setMaterials(resolved)
            setPricingPolicy(nextPricingPolicy)
            setPublicBlankPricingSnapshot({
                materials: resolved,
                pricingPolicy: nextPricingPolicy,
            })
            return resolved
        } catch (requestError) {
            setError(requestError?.response?.data?.message || 'Не удалось загрузить материалы камня')
            setMaterials((previous) => {
                const resolved = previous.length ? previous : LOCAL_FALLBACK_MATERIALS
                setPublicBlankPricingSnapshot({
                    materials: resolved,
                    pricingPolicy: getPublicBlankPricingSnapshot()?.pricingPolicy || {},
                })
                return resolved
            })
            return LOCAL_FALLBACK_MATERIALS
        } finally {
            setLoading(false)
        }
    }, [])

    useEffect(() => {
        reload()
    }, [reload])

    useEffect(() => {
        const handleFocus = () => reload()
        window.addEventListener('focus', handleFocus)
        return () => window.removeEventListener('focus', handleFocus)
    }, [reload])

    const materialsByKey = useMemo(() => {
        const result = Object.fromEntries(
            materials.map((material) => [material.key, material])
        )
        // Совместимость со старыми сохранёнными сценами без отдельного
        // пункта в интерфейсе.
        if (result.vinga) result.kapustinsky = result.vinga
        return result
    }, [materials])

    const texturePalette = useMemo(() => {
        const remoteStonePalette = {}
        materials.forEach((material) => {
            if (material.texturePreset?.url) {
                remoteStonePalette[material.name] = material.texturePreset
            }
        })

        const nonStoneLocalPalette = Object.fromEntries(
            Object.entries(fenceColorsUi || {}).filter(([, value]) => (
                typeof value === 'string' && value.startsWith('#')
            ))
        )

        if (Object.keys(remoteStonePalette).length) {
            return { ...remoteStonePalette, ...nonStoneLocalPalette }
        }

        return fenceColorsUi
    }, [materials])

    const value = useMemo(() => ({
        materials,
        materialsByKey,
        texturePalette,
        loading,
        error,
        defaultMaterialKey: materialsByKey[DEFAULT_KEY] ? DEFAULT_KEY : (materials[0]?.key || DEFAULT_KEY),
        pricingPolicy,
        reload,
    }), [materials, materialsByKey, texturePalette, loading, error, pricingPolicy, reload])

    return (
        <BlankMaterialCatalogContext.Provider value={value}>
            {children}
        </BlankMaterialCatalogContext.Provider>
    )
}

export const useBlankMaterialCatalog = () => useContext(BlankMaterialCatalogContext)

export default BlankMaterialCatalogContext
